<template>
    <div class="min-h-screen bg-gray-50 p-6">
        <div class="max-w-6xl mx-auto">
            <!-- 页面标题 -->
            <div class="mb-8">
                <div class="flex items-center justify-between">
                    <div>
                        <h1 class="text-3xl font-bold text-gray-900 mb-2">相关问题管理</h1>
                        <p class="text-gray-600">管理右侧相关问题显示内容，支持手动添加和批量导入</p>
                    </div>
                    <button
                        @click="goBack"
                        class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 flex items-center space-x-2"
                    >
                        <i class="fas fa-arrow-left"></i>
                        <span>返回主界面</span>
                    </button>
                </div>
            </div>

            <!-- 操作工具栏 -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <!-- 搜索框 -->
                    <div class="flex-1 max-w-md">
                        <div class="relative">
                            <input
                                v-model="searchKeyword"
                                @input="handleSearch"
                                type="text"
                                placeholder="搜索相关问题..."
                                class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                        </div>
                    </div>

                    <!-- 操作按钮 -->
                    <div class="flex flex-wrap gap-3">
                        <button
                            @click="showAddModal = true"
                            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                        >
                            <i class="fas fa-plus"></i>
                            <span>添加问题</span>
                        </button>
                        <button
                            @click="showImportModal = true"
                            class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center space-x-2"
                        >
                            <i class="fas fa-file-import"></i>
                            <span>批量导入</span>
                        </button>
                        <button
                            @click="loadQuestions"
                            class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 flex items-center space-x-2"
                        >
                            <i class="fas fa-sync-alt"></i>
                            <span>刷新</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- 统计信息 -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div class="flex items-center">
                        <div class="p-3 bg-blue-100 rounded-full">
                            <i class="fas fa-question-circle text-blue-600 text-xl"></i>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600">总问题数</p>
                            <p class="text-2xl font-bold text-gray-900">{{ totalQuestions }}</p>
                        </div>
                    </div>
                </div>
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div class="flex items-center">
                        <div class="p-3 bg-green-100 rounded-full">
                            <i class="fas fa-eye text-green-600 text-xl"></i>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600">显示问题数</p>
                            <p class="text-2xl font-bold text-gray-900">{{ displayedQuestions }}</p>
                        </div>
                    </div>
                </div>
                <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div class="flex items-center">
                        <div class="p-3 bg-purple-100 rounded-full">
                            <i class="fas fa-clock text-purple-600 text-xl"></i>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-600">最新更新时间</p>
                            <p class="text-sm font-bold text-gray-900">{{ lastUpdateTime }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 问题列表 -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200">
                <div class="p-6 border-b border-gray-200">
                    <h2 class="text-lg font-semibold text-gray-900">问题列表</h2>
                </div>

                <!-- 加载状态 -->
                <div v-if="loading" class="flex items-center justify-center py-12">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span class="ml-2 text-gray-500">加载中...</span>
                </div>

                <!-- 空状态 -->
                <div v-else-if="questions.length === 0" class="text-center py-12">
                    <i class="fas fa-question-circle text-6xl text-gray-300 mb-4"></i>
                    <h3 class="text-lg font-medium text-gray-900 mb-2">暂无相关问题</h3>
                    <p class="text-gray-500 mb-4">点击"添加问题"开始创建第一个相关问题</p>
                    <button
                        @click="showAddModal = true"
                        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                    >
                        添加问题
                    </button>
                </div>

                <!-- 问题列表 -->
                <div v-else class="divide-y divide-gray-200">
                    <div
                        v-for="question in questions"
                        :key="question.id"
                        class="p-6 hover:bg-gray-50 transition-colors duration-200"
                    >
                        <div class="flex items-start justify-between">
                            <div class="flex-1">
                                <p class="text-gray-900 font-medium mb-2">{{ question.content }}</p>
                                <div class="flex items-center space-x-4 text-sm text-gray-500">
                                    <span>
                                        <i class="fas fa-user mr-1"></i>
                                        {{ question.createdBy || '系统' }}
                                    </span>
                                    <span>
                                        <i class="fas fa-calendar mr-1"></i>
                                        {{ formatDate(question.createdTime) }}
                                    </span>
                                    <span v-if="question.updatedTime !== question.createdTime">
                                        <i class="fas fa-edit mr-1"></i>
                                        更新于 {{ formatDate(question.updatedTime) }}
                                    </span>
                                </div>
                            </div>
                            <div class="flex items-center space-x-2 ml-4">
                                <button
                                    @click="editQuestion(question)"
                                    class="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                                    title="编辑"
                                >
                                    <i class="fas fa-edit"></i>
                                </button>
                                <button
                                    @click="deleteQuestion(question)"
                                    class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                    title="删除"
                                >
                                    <i class="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 分页 -->
                <div v-if="totalPages > 1" class="p-6 border-t border-gray-200">
                    <div class="flex items-center justify-between">
                        <div class="text-sm text-gray-700">
                            显示第 {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, totalQuestions) }} 条，共 {{ totalQuestions }} 条
                        </div>
                        <div class="flex items-center space-x-2">
                            <button
                                @click="goToPage(currentPage - 1)"
                                :disabled="currentPage <= 1"
                                class="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                上一页
                            </button>
                            <span class="px-3 py-1 text-sm text-gray-700">
                                第 {{ currentPage }} 页，共 {{ totalPages }} 页
                            </span>
                            <button
                                @click="goToPage(currentPage + 1)"
                                :disabled="currentPage >= totalPages"
                                class="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                下一页
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 添加/编辑问题弹窗 -->
        <div v-if="showAddModal || showEditModal" class="fixed inset-0 flex items-center justify-center z-50">
            <div class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
            <div class="relative bg-white rounded-2xl p-8 w-[600px] max-w-[90vw] shadow-2xl">
                <div class="text-center mb-6">
                    <div class="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i class="fas fa-question-circle text-white text-2xl"></i>
                    </div>
                    <h3 class="text-2xl font-bold text-gray-900 mb-2">
                        {{ showAddModal ? '添加相关问题' : '编辑相关问题' }}
                    </h3>
                    <p class="text-gray-600">请输入问题的具体内容</p>
                </div>

                <form @submit.prevent="saveQuestion" class="space-y-6">
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-2">问题内容</label>
                        <textarea
                            v-model="questionForm.content"
                            rows="4"
                            required
                            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            placeholder="请输入问题内容..."
                        ></textarea>
                    </div>

                    <div class="flex justify-end space-x-3">
                        <button
                            type="button"
                            @click="closeModal"
                            class="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                        >
                            取消
                        </button>
                        <button
                            type="submit"
                            :disabled="!questionForm.content.trim()"
                            class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            {{ showAddModal ? '添加' : '保存' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <!-- 批量导入弹窗 -->
        <div v-if="showImportModal" class="fixed inset-0 flex items-center justify-center z-50">
            <div class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
            <div class="relative bg-white rounded-2xl p-8 w-[600px] max-w-[90vw] shadow-2xl">
                <div class="text-center mb-6">
                    <div class="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i class="fas fa-file-import text-white text-2xl"></i>
                    </div>
                    <h3 class="text-2xl font-bold text-gray-900 mb-2">批量导入相关问题</h3>
                    <p class="text-gray-600">每行一个问题，支持批量导入</p>
                </div>

                <form @submit.prevent="importQuestions" class="space-y-6">
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-2">问题内容（每行一个）</label>
                        <textarea
                            v-model="importForm.content"
                            rows="8"
                            required
                            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                            placeholder="请输入问题内容，每行一个问题：&#10;如何做好党支部换届工作？&#10;党员发展的基本程序是什么？&#10;党费收缴使用的规定有哪些？"
                        ></textarea>
                        <p class="text-sm text-gray-500 mt-2">提示：每行输入一个问题，系统会自动识别并导入</p>
                    </div>

                    <div class="flex justify-end space-x-3">
                        <button
                            type="button"
                            @click="closeImportModal"
                            class="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                        >
                            取消
                        </button>
                        <button
                            type="submit"
                            :disabled="!importForm.content.trim()"
                            class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            导入
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import request from '@/utils/request'

export default {
    name: 'RelatedQuestionManagement',
    data() {
        return {
            questions: [],
            loading: false,
            searchKeyword: '',
            currentPage: 1,
            pageSize: 10,
            totalQuestions: 0,
            totalPages: 0,
            displayedQuestions: 0,
            lastUpdateTime: '',
            
            // 弹窗状态
            showAddModal: false,
            showEditModal: false,
            showImportModal: false,
            
            // 表单数据
            questionForm: {
                id: null,
                content: ''
            },
            importForm: {
                content: ''
            }
        }
    },
    mounted() {
        this.loadQuestions()
    },
    methods: {
        // 返回主界面
        goBack() {
            this.$router.push('/chat')
        },
        
        // 加载问题列表
        async loadQuestions() {
            try {
                this.loading = true
                const response = await request.get('/api/related-questions', {
                    params: {
                        page: this.currentPage - 1,
                        size: this.pageSize
                    }
                })
                
                if (response.data.code === 200) {
                    const data = response.data.data
                    this.questions = data.content || []
                    this.totalQuestions = data.totalElements || 0
                    this.totalPages = data.totalPages || 0
                    this.displayedQuestions = Math.min(5, this.totalQuestions)
                    
                    // 计算最新更新时间
                    if (this.questions.length > 0) {
                        const latestQuestion = this.questions[0]
                        this.lastUpdateTime = this.formatDate(latestQuestion.updatedTime || latestQuestion.createdTime)
                    }
                } else {
                    alert(response.data.message || '加载问题列表失败')
                }
            } catch (error) {
                console.error('加载问题列表失败:', error)
                alert('加载问题列表失败')
            } finally {
                this.loading = false
            }
        },
        
        // 搜索问题
        async handleSearch() {
            if (this.searchKeyword.trim()) {
                try {
                    this.loading = true
                    const response = await request.get('/api/related-questions/search', {
                        params: {
                            keyword: this.searchKeyword
                        }
                    })
                    
                    if (response.data.code === 200) {
                        this.questions = response.data.data || []
                        this.totalQuestions = this.questions.length
                        this.totalPages = 1
                        this.currentPage = 1
                    } else {
                        alert(response.data.message || '搜索失败')
                    }
                } catch (error) {
                    console.error('搜索失败:', error)
                    alert('搜索失败')
                } finally {
                    this.loading = false
                }
            } else {
                this.loadQuestions()
            }
        },
        
        // 跳转到指定页
        goToPage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page
                this.loadQuestions()
            }
        },
        
        // 编辑问题
        editQuestion(question) {
            this.questionForm = {
                id: question.id,
                content: question.content
            }
            this.showEditModal = true
        },
        
        // 删除问题
        async deleteQuestion(question) {
            try {
                const confirmed = confirm(`确定要删除问题"${question.content}"吗？`)
                if (!confirmed) {
                    return
                }
                
                const response = await request.delete(`/api/related-questions/${question.id}`)
                
                if (response.data.code === 200) {
                    alert('删除成功')
                    this.loadQuestions()
                } else {
                    alert(response.data.message || '删除失败')
                }
            } catch (error) {
                console.error('删除问题失败:', error)
                alert('删除失败')
            }
        },
        
        // 保存问题
        async saveQuestion() {
            try {
                let response
                if (this.showAddModal) {
                    response = await request.post('/api/related-questions', {
                        content: this.questionForm.content.trim()
                    })
                } else {
                    response = await request.put(`/api/related-questions/${this.questionForm.id}`, {
                        content: this.questionForm.content.trim()
                    })
                }
                
                if (response.data.code === 200) {
                    alert(this.showAddModal ? '添加成功' : '更新成功')
                    this.closeModal()
                    this.loadQuestions()
                } else {
                    alert(response.data.message || '保存失败')
                }
            } catch (error) {
                console.error('保存问题失败:', error)
                alert('保存失败')
            }
        },
        
        // 批量导入问题
        async importQuestions() {
            try {
                const contents = this.importForm.content
                    .split('\n')
                    .map(line => line.trim())
                    .filter(line => line.length > 0)
                
                if (contents.length === 0) {
                    alert('请输入至少一个问题')
                    return
                }
                
                const response = await request.post('/api/related-questions/batch-import', {
                    contents: contents
                })
                
                if (response.data.code === 200) {
                    alert(response.data.message || '导入成功')
                    this.closeImportModal()
                    this.loadQuestions()
                } else {
                    alert(response.data.message || '导入失败')
                }
            } catch (error) {
                console.error('导入问题失败:', error)
                alert('导入失败')
            }
        },
        
        // 关闭弹窗
        closeModal() {
            this.showAddModal = false
            this.showEditModal = false
            this.questionForm = {
                id: null,
                content: ''
            }
        },
        
        // 关闭导入弹窗
        closeImportModal() {
            this.showImportModal = false
            this.importForm.content = ''
        },
        
        // 格式化日期
        formatDate(dateString) {
            if (!dateString) return ''
            
            try {
                const date = new Date(dateString)
                return date.toLocaleString('zh-CN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                })
            } catch (error) {
                console.error('日期格式化失败:', error)
                return dateString
            }
        }
    }
}
</script>

<style scoped>
/* 自定义样式 */
</style>
